/**
 * Pure seeded randomness (ADR 0001). A stream is four 32-bit integers stored
 * in the run state; drawing a number returns the value and the next stream.
 * Integer and bitwise operations only, so it replays identically everywhere.
 *
 * The generator is sfc32 (Chris Doty-Humphrey's Small Fast Counter).
 */

/** The state of one stream: sfc32's a, b, c, d as signed 32-bit integers. */
export type RngState = readonly [number, number, number, number];

const TWO_POW_32 = 0x100000000;

const step = (s: RngState): { value: number; next: RngState } => {
  const [a, b, c, d] = s;
  const t = (((a + b) | 0) + d) | 0;
  const na = b ^ (b >>> 9);
  const nb = (c + (c << 3)) | 0;
  const nc = (((c << 21) | (c >>> 11)) + t) | 0;
  return { value: t >>> 0, next: [na, nb, nc, (d + 1) | 0] };
};

/**
 * A stream from a 32-bit seed and a stream number, so one floor seed gives
 * independent map, combat, ai and loot streams. Mixes 15 steps before use.
 */
export const seedRng = (seed: number, stream: number): RngState => {
  if (!Number.isInteger(seed)) throw new RangeError('seedRng: seed must be an integer');
  if (!Number.isInteger(stream) || stream < 0) throw new RangeError('seedRng: stream must be a non-negative integer');
  let s: RngState = [0x9e3779b9 | 0, 0x243f6a88 | 0, (0xb7e15162 ^ Math.imul(stream + 1, 0x85ebca6b)) | 0, seed | 0];
  for (let i = 0; i < 15; i += 1) s = step(s).next;
  return s;
};

/** A uniformly distributed integer in `[0, maxExclusive)`, without modulo bias. */
export const nextInt = (s: RngState, maxExclusive: number): { value: number; next: RngState } => {
  if (!Number.isInteger(maxExclusive) || maxExclusive < 1 || maxExclusive > TWO_POW_32) {
    throw new RangeError('nextInt: maxExclusive must be an integer in [1, 2^32]');
  }
  const limit = TWO_POW_32 - (TWO_POW_32 % maxExclusive);
  let cur = s;
  for (;;) {
    const r = step(cur);
    cur = r.next;
    if (r.value < limit) return { value: r.value % maxExclusive, next: cur };
  }
};

/** True with probability `chancePercent` / 100. Draws nothing at 0 or 100. */
export const roll = (s: RngState, chancePercent: number): { hit: boolean; next: RngState } => {
  if (chancePercent >= 100) return { hit: true, next: s };
  if (chancePercent <= 0) return { hit: false, next: s };
  const r = nextInt(s, 100);
  return { hit: r.value < chancePercent, next: r.next };
};

/** One index from a weighted list, weights being positive integers. */
export const pickWeighted = (s: RngState, weights: readonly number[]): { index: number; next: RngState } => {
  let total = 0;
  for (const w of weights) total += w;
  if (total <= 0) throw new RangeError('pickWeighted: weights must sum to a positive integer');
  const r = nextInt(s, total);
  let acc = 0;
  for (let i = 0; i < weights.length; i += 1) {
    acc += weights[i] ?? 0;
    if (r.value < acc) return { index: i, next: r.next };
  }
  return { index: weights.length - 1, next: r.next };
};

/** A random integer in `[min, max]` inclusive. */
export const between = (s: RngState, min: number, max: number): { value: number; next: RngState } => {
  if (max < min) throw new RangeError('between: max < min');
  const r = nextInt(s, max - min + 1);
  return { value: min + r.value, next: r.next };
};
